import { Field, Formik } from 'formik'
import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { DashboardBackButton } from '@components/utils'
import {
  Button,
  ButtonClean,
  Fieldset,
  HeadContent,
  Input,
  Subsection,
  Tag,
} from '@fourthwall/components'
import { routing } from '@utils/routing'

import { mapStatusToTagProps } from '../../utils/mapStatusToTagProps'
import type { SettingsFormValues } from './AppsBeehiiv.types'
import { ConnectValidationSchema } from './consts'
import { useBeehiiv } from './hooks/useBeehiiv'

export const AppsBeehiiv = () => {
  const { beehiiv } = useBeehiiv()

  const renderStatusTag = () => {
    const props = mapStatusToTagProps(beehiiv.status)

    if (!props) return null

    return (
      <div style={{ marginBottom: 20 }}>
        <Tag {...props} size="large" display="inline-flex" />
      </div>
    )
  }

  const renderForm = () => {
    if (beehiiv.isLoading || beehiiv.status) {
      return null
    }

    return (
      <Subsection title="Connection">
        <Formik<SettingsFormValues>
          initialValues={beehiiv.connection.initialValues}
          validationSchema={ConnectValidationSchema}
          onSubmit={beehiiv.connection.onSubmit}
        >
          {(connectionForm) => (
            <form onSubmit={connectionForm.handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                Connect your beehiiv account using your API key and Publication Id from the{' '}
                <ButtonClean
                  label="Beehiiv API Docs"
                  href={routing.external.beehiiv.apiDocs}
                  target="_blank"
                />
                . Be sure to use the V2 Publication Id.
              </div>
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field component={Input} name="apiKey" label="API key" />
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field component={Input} name="publicationId" label="Publication Id" />
                  </Fieldset.Item>
                </Fieldset.Row>
              </Fieldset>
              <div style={{ marginTop: 16 }}>
                <Button
                  type="submit"
                  label="Connect to beehiiv"
                  appearance="primary"
                  loading={connectionForm.isSubmitting}
                />
              </div>
            </form>
          )}
        </Formik>
      </Subsection>
    )
  }

  const renderConnectedContent = () => {
    // if (beehiiv.isLoading || beehiiv.status === 'NOT_CONNECTED') {
    if (beehiiv.isLoading || !beehiiv.status) {
      return null
    }

    return (
      <Subsection title="Connection">
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 16 }}>
            Connected to your beehiiv account.{' '}
            <ButtonClean label="Disconnect" onClick={beehiiv.onDisconnect} />
          </p>
        </div>
      </Subsection>
    )
  }

  return (
    <>
      <HeadContent title="beehiiv" />
      <PageLayout size="tiny">
        <Dashboard.Section
          name="beehiiv"
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          <Subsection>
            {renderStatusTag()}
            <div>
              Use beehiiv to automatically sync customer emails. beehiiv allows you to send
              newsletter updates to all supporters that have opted into email communications.{' '}
              <ButtonClean
                label="Learn More"
                href={routing.external.helpCenter.beehiiv}
                target="_blank"
              />
              .
            </div>
          </Subsection>
          {renderForm()}
          {renderConnectedContent()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
